import React, { memo, useRef, useState } from "react";
import styled from "styled-components";
import { Icon } from "@slid/slid-ips";
import { getEventLocationMode } from "utils/utils";
import { trackEvent } from "utils/eventTracking";
import { useTranslation } from "react-i18next";
import { eventTypes } from "types/eventTracking";

const MIN_TEXTAREA_HEIGHT = 18;
const MAX_TEXTAREA_HEIGHT = 216;
const FULL_VERSION_INPUT_SIDE_HEIGHT = 26;
const POPUP_INPUT_SIDE_HEIGHT = 18;

export const ChatInput = memo(
  ({
    isLoading,
    onSendChatMessage,
    chatError,
    isDarkTheme,
    isDesktopVideonote = false,
    setInputHeight,
  }: {
    isLoading: boolean;
    onSendChatMessage: (message: string) => void;
    chatError: boolean | null;
    isDarkTheme: boolean;
    isDesktopVideonote?: boolean;
    setInputHeight: (height: number) => void;
  }) => {
    const { t } = useTranslation("NoteMate");
    const inputBoxRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [userTextInput, setUserTextInput] = useState("");
    const [isLongInput, setIsLongInput] = useState(false);

    React.useLayoutEffect(() => {
      if (!textareaRef.current || !inputBoxRef.current) return;
      // Reset height
      textareaRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
      setInputHeight(MIN_TEXTAREA_HEIGHT);

      // Set height
      const inputSideHeight = isDesktopVideonote ? POPUP_INPUT_SIDE_HEIGHT : FULL_VERSION_INPUT_SIDE_HEIGHT;
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
      inputBoxRef.current.style.height = `${Math.min(scrollHeight, MAX_TEXTAREA_HEIGHT) + inputSideHeight}px`;

      setInputHeight(Math.min(scrollHeight, MAX_TEXTAREA_HEIGHT) + inputSideHeight);
      setIsLongInput(textareaRef.current.clientHeight > MIN_TEXTAREA_HEIGHT * 2);
    }, [userTextInput, isDesktopVideonote]);

    const handleMessageSubmit = () => {
      if (isLoading) return;
      if (chatError) return;

      if (userTextInput.trim().length > 0) {
        onSendChatMessage(userTextInput);

        trackEvent({
          eventType: eventTypes.add.ADD_CHAT_MESSAGE_IN_NOTE_MATE_CHAT,
          eventProperties: {
            from: getEventLocationMode(),
            message: userTextInput,
          },
        });
      }

      setUserTextInput("");
      return;
    };

    const handleInputChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      if (isLoading) return;
      e.preventDefault();
      setUserTextInput(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const cmdPressed = e.ctrlKey || e.metaKey;

      if (e.key === "Enter" && !e.shiftKey) {
        e?.preventDefault();
        e?.stopPropagation();
        e?.nativeEvent.stopImmediatePropagation();
        handleMessageSubmit();
      }

      if (e.key === "z" && cmdPressed) {
        const textArea = e.target as HTMLTextAreaElement;
        const caretPos = textArea.selectionStart;
        if (caretPos === 0) e.preventDefault();
        // to prevent undo on editor
        e.nativeEvent.stopImmediatePropagation();
        e.stopPropagation();
      }
    };

    if (isDesktopVideonote) {
      return (
        <PopupChatWrapper isDarkTheme={isDarkTheme}>
          <PopupInputContainer ref={inputBoxRef} isLongInput={isLongInput}>
            <PopupInputBox ref={textareaRef} value={userTextInput} placeholder={t("ChatInputPlaceholder")} onInput={handleInputChange} onKeyDown={handleKeyDown} autoFocus isDarkTheme={isDarkTheme} />
            <SendIconContainer
              isInputEmpty={userTextInput.length === 0}
              onClick={(e) => {
                e.preventDefault();
                handleMessageSubmit();
              }}
              isDisabled={userTextInput.length === 0}
            >
              <Icon icon="arrowUp24" color={userTextInput.length === 0 ? "var(--gray10)" : "white"} />
            </SendIconContainer>
          </PopupInputContainer>
        </PopupChatWrapper>
      );
    }

    return (
      <ChatWrapper isDarkTheme={isDarkTheme}>
        <InputContainer ref={inputBoxRef} isLongInput={isLongInput}>
          <InputBox ref={textareaRef} value={userTextInput} placeholder={t("ChatInputPlaceholder")} onInput={handleInputChange} onKeyDown={handleKeyDown} autoFocus isDarkTheme={isDarkTheme} />
          <SendIconContainer
            isInputEmpty={userTextInput.length === 0}
            onClick={(e) => {
              e.preventDefault();
              handleMessageSubmit();
            }}
            isDisabled={userTextInput.length === 0}
          >
            <Icon icon="arrowUp24" color={userTextInput.length === 0 ? "var(--gray10)" : "white"} />
          </SendIconContainer>
        </InputContainer>
      </ChatWrapper>
    );
  }
);

const ChatWrapper = styled.div<{ isDarkTheme: boolean }>`
  position: fixed;
  bottom: 0;
  width: 400px;
  padding: 48px 20px 24px 20px;
  ${({ isDarkTheme }) =>
    `
    background: ${isDarkTheme ? "linear-gradient(180deg, rgba(20, 23, 26, 0) 0%, rgba(20, 23, 26, 0.6) 10%, rgba(20, 23, 26, 0.85) 21.5%, #14171a 30%)" : "linear-gradient(180deg, rgba(248, 249, 250, 0.00) 0%, rgba(248, 249, 250, 0.60) 10%, rgba(248, 249, 250, 0.85) 21.5%, #F8F9FA 30%)"};
  `}
`;

const PopupChatWrapper = styled(ChatWrapper)`
  position: absolute;
  width: 300px;
  padding: 40px 16px 20px;
  border-radius: 0px 0px 16px 16px;
`;

const InputContainer = styled.div<{ applicationType?: string; isLongInput: boolean }>`
  width: 100%;
  height: 48px;
  max-height: 240px; /* 최대 높이 (12줄 크기) */

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 15px;
  border: 2px solid var(--Primary-Colors-Blue6, #84bff7);

  ${({ isLongInput }) =>
    isLongInput &&
    `
    align-items: flex-start;
  `}
`;

const PopupInputContainer = styled(InputContainer)`
  height: 40px;
  padding: 8px 12px 8px 16px;
  align-items: center;
  border-radius: 12px;
  gap: 12px;

  ${({ isLongInput }) =>
    isLongInput &&
    `
    align-items: flex-start;
  `}
`;

const InputBox = styled.textarea<{ isDarkTheme: boolean }>`
  flex: 1 0 0;
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  overflow: auto; /* 최대 높이 도달 시 스크롤 생성 */

  color: ${({ isDarkTheme }) => (isDarkTheme ? "var(--white)" : "var(--gray18)")};
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* en/Body3_400 */
  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 138.462% */

  resize: none; /* 사용자가 사이즈 조절하지 못하도록 */

  &::placeholder {
    color: ${({ isDarkTheme }) => (isDarkTheme ? "var(--gray11)" : "var(--gray7)")};
    font-weight: 500;
  }

  // 스크롤 바 숨기기
  &::-webkit-scrollbar {
    width: 0; // 스크롤 바 너비를 0으로 설정
    background: transparent; // 배경을 투명으로 설정
  }
`;

const PopupInputBox = styled(InputBox)`
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 17px; /* 141.667% */
`;

const SendIconContainer = styled.div<{ isInputEmpty: boolean; isDisabled: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: ${(props) => (props.isDisabled ? "initial" : "pointer")};
  background-color: ${(props) => (props.isInputEmpty ? "" : "var(--blue9)")};

  &:hover {
    background-color: ${(props) => (props.isInputEmpty ? "" : "var(--blue7)")};
  }
  &:active {
    background-color: ${(props) => (props.isInputEmpty ? "" : "var(--blue7)")};
  }
`;
